import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import './Profile.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Profile() {
  const [preferences, setPreferences] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [availableSources, setAvailableSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const { token, user } = useAuthStore();

  useEffect(() => {
    fetchPreferences();
    fetchSchedule();
    fetchAvailableSources();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API_BASE}/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // isps_monitored is stored as a comma-separated string (or null);
      // normalize to an array here so the rest of this component can
      // treat it consistently instead of mixing string/array handling.
      const raw = response.data.isps_monitored;
      const isps_monitored = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
      setPreferences({ ...response.data, isps_monitored });
    } catch (error) {
      toast.error('Error loading preferences');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSources = async () => {
    try {
      const response = await axios.get(`${API_BASE}/user/available-sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableSources(response.data || []);
    } catch (error) {
      console.error('Error loading available sources:', error);
    }
  };

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API_BASE}/schedule/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedule(response.data);
    } catch (error) {
      console.error('Error loading schedule:', error);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_BASE}/user/preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferences saved!');
    } catch (error) {
      toast.error('Error saving preferences');
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingSchedule(true);
      const response = await axios.put(
        `${API_BASE}/schedule/schedule`,
        schedule,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setSchedule(response.data.schedule);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      setChangingPassword(true);
      await axios.post(
        `${API_BASE}/auth/change-password`,
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error changing password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSourceChange = (source) => {
    setPreferences(prev => ({
      ...prev,
      isps_monitored: prev.isps_monitored?.includes(source)
        ? prev.isps_monitored.filter(s => s !== source)
        : [...(prev.isps_monitored || []), source]
    }));
  };

  if (loading) return <div className="profile-loading">Loading preferences...</div>;

  return (
    <div className="profile-container">
      <h1>Account Settings</h1>

      <div className="profile-content">
        {/* User Info Section */}
        <div className="profile-section">
          <h2>Account Information</h2>
          <div className="info-display">
            <div className="info-item">
              <label>Email:</label>
              <span>{user?.email}</span>
            </div>
          </div>
        </div>

        {/* Preferences Section */}
        {preferences && (
          <div className="profile-section">
            <h2>Notification Preferences</h2>
            <div className="preferences-form">
              <div className="form-group">
                <label>Digest Frequency</label>
                <select
                  value={preferences.digest_frequency}
                  onChange={(e) => setPreferences({
                    ...preferences,
                    digest_frequency: e.target.value
                  })}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div className="form-group">
                <label>Email Sources - Select which updates you want to receive</label>
                <p className="sources-hint">
                  Leave all unchecked to receive updates from every source (recommended).
                  Checking any box restricts your digest to only the checked sources.
                </p>
                <div className="sources-grid">
                  {availableSources.map(source => (
                    <label key={source.source} className="source-checkbox">
                      <input
                        type="checkbox"
                        checked={preferences.isps_monitored?.includes(source.source) || false}
                        onChange={() => handleSourceChange(source.source)}
                      />
                      <span className="source-label">{source.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={preferences.email_notifications || false}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      email_notifications: e.target.checked
                    })}
                  />
                  Email Notifications
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={preferences.instant_alerts_enabled || false}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      instant_alerts_enabled: e.target.checked
                    })}
                  />
                  Instant Critical Alerts
                </label>
                <p className="sources-hint">
                  Send a separate email immediately when a critical or high-severity update is found,
                  instead of waiting for your next scheduled digest.
                </p>
              </div>

              <button onClick={handleSave} className="save-btn">Save Preferences</button>
            </div>
          </div>
        )}

        {/* Automated Digest Schedule Section */}
        {schedule && (
          <div className="profile-section">
            <h2>Automated Digest Schedule</h2>
            <div className="schedule-form">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={schedule.auto_send_enabled || false}
                    onChange={(e) => setSchedule({
                      ...schedule,
                      auto_send_enabled: e.target.checked
                    })}
                  />
                  Enable Automated Digests
                </label>
                <small>Automatically send digests at your preferred time</small>
              </div>

              {schedule.auto_send_enabled && (
                <>
                  <div className="form-group">
                    <label htmlFor="scheduled-time">Preferred Time</label>
                    <input
                      id="scheduled-time"
                      type="time"
                      value={schedule.scheduled_time || '09:00'}
                      onChange={(e) => setSchedule({
                        ...schedule,
                        scheduled_time: e.target.value
                      })}
                    />
                    <small>Digests will be sent at this time each day</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="timezone">Timezone</label>
                    <select
                      id="timezone"
                      value={schedule.timezone || 'UTC'}
                      onChange={(e) => setSchedule({
                        ...schedule,
                        timezone: e.target.value
                      })}
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="America/Anchorage">Alaska Time</option>
                      <option value="Europe/London">UK Time</option>
                      <option value="Europe/Paris">Central European Time</option>
                      <option value="Asia/Tokyo">Japan Time</option>
                      <option value="Asia/Hong_Kong">Hong Kong Time</option>
                      <option value="Australia/Sydney">Sydney Time</option>
                      <option value="Australia/Melbourne">Melbourne Time</option>
                      <option value="Asia/Dubai">Dubai Time</option>
                      <option value="Asia/Kolkata">India Time</option>
                      <option value="Asia/Singapore">Singapore Time</option>
                    </select>
                  </div>

                  <div className="schedule-info">
                    {schedule.last_sent_at && (
                      <p className="info-text">
                        <strong>Last sent:</strong> {new Date(schedule.last_sent_at).toLocaleString()}
                      </p>
                    )}
                    {schedule.next_send_at && (
                      <p className="info-text">
                        <strong>Next scheduled:</strong> {new Date(schedule.next_send_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </>
              )}

              <button 
                onClick={handleSaveSchedule} 
                className="save-btn"
                disabled={savingSchedule}
              >
                {savingSchedule ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </div>
        )}

        {/* Change Password Section */}
        <div className="profile-section">
          <h2>Change Password</h2>
          <form onSubmit={handlePasswordChange} className="password-form">
            <div className="form-group">
              <label htmlFor="current-password">Current Password</label>
              <input
                id="current-password"
                type="password"
                placeholder="Enter your current password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({
                  ...passwordForm,
                  currentPassword: e.target.value
                })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="new-password">New Password</label>
              <input
                id="new-password"
                type="password"
                placeholder="Enter your new password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({
                  ...passwordForm,
                  newPassword: e.target.value
                })}
                required
              />
              <small>Minimum 6 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">Confirm New Password</label>
              <input
                id="confirm-password"
                type="password"
                placeholder="Confirm your new password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({
                  ...passwordForm,
                  confirmPassword: e.target.value
                })}
                required
              />
            </div>

            <button 
              type="submit" 
              className="change-password-btn"
              disabled={changingPassword}
            >
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Profile;
